import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import moment from "moment";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import Logo from "../../img/powered_by_hg_logo.png";
import popUpIcon from "../../img/popup-open-icon.png";
import EmbedGigGridOrganisationDetails from "./details/EmbedGigGridOrganisationDetails";
import agent from "../../agent";
import "./EmbedGigGrid.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const PAGE_SIZE = 12;
const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const toApiDate = input => {
    if (!input) return "";
    const parts = input.split("-");
    if (parts.length !== 3) return "";
    return `${parts[1]}-${parts[2]}-${parts[0]}`;
};

@inject("embedGigStore", "gigPromotionStore")
@withRouter
@observer
class EmbedGigGridOrganisation extends Component {
    sentinel = null;
    observer = null;

    state = {
        finishLoading:    false,
        performData:      [],
        openDialogDetails: false,
        data:             null,
        tableBgColor:     "#ff0c9c",
        tableFtColor:     "#fff",
        tableWidgetBgColor: "transparent",
        filterBgColor:    null,
        enabledTransparent: false,
        brokenImages:     {},
        page:             1,
        hasMore:          true,
        isLoadingMore:    false,
        fromDate:         "",
        toDate:           "",
        dayOfWeek:        "",
        venueId:          "",
        ticketingFilter:  "all",
        utcOffset:        null,
        availableVenues:  {},
        availableDays:    [],
    };

    componentDidMount() {
        document.body.className = "bodyStyle";
        this.initFromUrl();
        this.setupObserver();
    }

    componentWillUnmount() {
        if (this.observer) this.observer.disconnect();
    }

    componentDidUpdate(prevProps) {
        if (this.props.match.params.id !== prevProps.match.params.id) {
            this.initFromUrl();
        }
    }

    setupObserver = () => {
        if (!window.IntersectionObserver) return;
        this.observer = new IntersectionObserver(entries => {
            if (
                entries[0].isIntersecting &&
                this.state.hasMore &&
                !this.state.isLoadingMore &&
                this.state.finishLoading &&
                !this.state.openDialogDetails
            ) {
                this.loadPage(this.state.page + 1);
            }
        }, { rootMargin: "200px" });
        if (this.sentinel) this.observer.observe(this.sentinel);
    };

    setSentinel = el => {
        this.sentinel = el;
        if (el && this.observer) this.observer.observe(el);
    };

    initFromUrl = () => {
        const organisationId = this.props.match.params.id;
        const params = new URLSearchParams(this.props.location.search);

        const utcOffset       = params.get("utcOffset") != null ? params.get("utcOffset") : null;
        const fromDate        = params.get("fromDate") || "";
        const toDate          = params.get("toDate") || "";
        const dayOfWeek       = params.get("dayOfWeek") || "";
        const venueId         = params.get("venueId") || "";
        const ticketingFilter = params.get("ticketingFilter") || "all";

        this.props.embedGigStore.loadEmbedCodeOrganisation(organisationId).then(() => {
            const store = this.props.embedGigStore;
            this.setState({
                tableBgColor:       store.tableBgColor || "#ff0c9c",
                tableFtColor:       store.tableFtColor || "#fff",
                tableWidgetBgColor: store.tableWidgetBgColor
                    ? "#" + store.tableWidgetBgColor.replace("#", "")
                    : "transparent",
                filterBgColor:      store.filterBgColor || null,
                enabledTransparent: store.enabledTransparent || false,
            });
        });

        this.setState(
            { fromDate, toDate, dayOfWeek, venueId, ticketingFilter, utcOffset,
              performData: [], page: 1, hasMore: true, finishLoading: false,
              availableVenues: {}, availableDays: [] },
            () => {
                this.loadPage(1);
                this.loadVenueRoster(organisationId);
            }
        );
    };

    loadVenueRoster = organisationId => {
        agent.EmbedGig.getOrganisationVenues(organisationId)
            .then(response => {
                if (!response || !response.length) return;
                const venues = {};
                response.forEach(item => {
                    const v = item.venue || item;
                    if (v && v.id) {
                        venues[v.id] = {
                            id:   v.id,
                            name: v.profileName || v.profileNameString || v.name || String(v.id),
                        };
                    }
                });
                this.setState(prev => ({
                    availableVenues: { ...venues, ...prev.availableVenues },
                }));
            })
            .catch(() => {});
    };

    buildParams = page => {
        const { venueId, ticketingFilter, utcOffset, dayOfWeek, fromDate, toDate } = this.state;
        const startDate = fromDate
            ? toApiDate(fromDate)
            : moment().startOf("month").format("MM-DD-YYYY");
        const endDate = toDate
            ? toApiDate(toDate)
            : moment().add(4, "months").endOf("month").format("MM-DD-YYYY");

        const effectiveUtcOffset = utcOffset != null ? utcOffset : new Date().getTimezoneOffset();

        const p = { startDate, endDate, page, take: PAGE_SIZE, utcOffset: effectiveUtcOffset };
        if (venueId)                        p.venueId     = venueId;
        if (ticketingFilter === "ticketed") p.isTicketing = true;
        if (ticketingFilter === "free")     p.isTicketing  = false;
        if (dayOfWeek)                      p.dayOfWeek   = dayOfWeek;
     
        return p;
    };

    loadPage = page => {
        const { isLoadingMore } = this.state;
        if (isLoadingMore && page > 1) return;

        const isFirst = page === 1;
        this.setState(isFirst
            ? { finishLoading: false, isLoadingMore: false }
            : { isLoadingMore: true }
        );

        const organisationId = this.props.match.params.id;
        this.props.gigPromotionStore
            .loadOrganisationEmbedGigs(organisationId, this.buildParams(page))
            .then(gigs => {
                const items   = this.processGigs(gigs || []);
                const hasMore = (gigs || []).length === PAGE_SIZE;

                const newVenues = {};
                (gigs || []).forEach(g => {
                    if (g.venue && g.venue.id) {
                        newVenues[g.venue.id] = {
                            id:   g.venue.id,
                            name: g.venue.name || g.venue.profileName || g.venueName || String(g.venue.id),
                        };
                    }
                });

                this.setState(prev => {
                    const daySet = new Set(prev.availableDays);
                    items.forEach(g => daySet.add(g.day));
                    const availableDays = DAY_ORDER.filter(d => daySet.has(d));

                    return {
                        performData:    isFirst ? items : [...prev.performData, ...items],
                        page,
                        hasMore,
                        finishLoading:  true,
                        isLoadingMore:  false,
                        availableVenues: { ...prev.availableVenues, ...newVenues },
                        availableDays,
                    };
                }, () => {
                    if (this.state.hasMore && this.sentinel && this.observer && !this.state.openDialogDetails) {
                        this.observer.unobserve(this.sentinel);
                        this.observer.observe(this.sentinel);
                    }
                });
            })
            .catch(() => {
                this.setState({ finishLoading: true, isLoadingMore: false, hasMore: false });
            });
    };

    applyFilters = updates => {
        this.setState(
            { ...updates, performData: [], page: 1, hasMore: true,
              finishLoading: false, availableDays: [] },
            () => this.loadPage(1)
        );
    };

    applyDayFilter = dayOfWeek => {
        this.setState(
            { dayOfWeek, performData: [], page: 1, hasMore: true, finishLoading: false },
            () => this.loadPage(1)
        );
    };

    clearFilters = () => {
        this.setState(
            { fromDate: "", toDate: "", dayOfWeek: "", venueId: "", ticketingFilter: "all",
              performData: [], page: 1, hasMore: true,
              finishLoading: false, availableDays: [] },
            () => this.loadPage(1)
        );
    };

    processGigs = gigs => {
        if (!gigs || !gigs.length) return [];
        return gigs.map(gig => {
            const dateStart = moment(gig.dateStart ? gig.dateStart.substring(0, 10) : null, "YYYY-MM-DD");
            const actName   = gig.actName || (gig.act && (gig.act.actName || gig.act.profileName)) || "Unknown Act";
            const venueName = gig.venueName || (gig.venue && (gig.venue.name || gig.venue.profileName)) || "";
            const cardImage = gig.resolvedImageUrl
                || (gig.act   && gig.act.resolvedProfileImage)
                || (gig.venue && gig.venue.resolvedBackgroundImage)
                || BASE_URL + "hero2.jpg";
            return {
                eventId:         gig.id,
                year:            dateStart.format("YYYY"),
                month:           dateStart.format("MMMM"),
                date:            dateStart.format("D"),
                day:             dateStart.format("dddd"),
                dayShort:        dateStart.format("ddd"),
                startTimeString: gig.timeStart ? moment(gig.timeStart, "HH:mm:ss").format("h.mma") : "",
                time:            gig.timeStart
                    ? moment(gig.timeStart, "HH:mm:ss").format("h.mma") +
                      (gig.timeEnd ? " - " + moment(gig.timeEnd, "HH:mm:ss").format("h.mma") : "")
                    : "",
                actName,
                venueName,
                cardImage,
                actId:           (gig.act && gig.act.id) || gig.actId || null,
                ticketUrl:       gig.ticketUrl || "",
                ticketPrice:     gig.ticketPrice,
                bookTableUrl:    (gig.venue && gig.venue.bookTableUrl) || "",
                resolvedImageUrl: gig.resolvedImageUrl,
                act:             gig.act,
                venue:           gig.venue,
                actDescription:  gig.actDescription,
            };
        });
    };

    handleImgError    = key  => this.setState(prev => ({ brokenImages: { ...prev.brokenImages, [key]: true } }));
    handleOpenDetails = data => this.setState({ openDialogDetails: true, data });
    handleCloseDetails = () => {
        this.setState({ openDialogDetails: false, data: null }, () => {
            if (this.state.hasMore && this.sentinel && this.observer) {
                this.observer.unobserve(this.sentinel);
                this.observer.observe(this.sentinel);
            }
        });
    };

    renderFilterBar() {
        const { fromDate, toDate, dayOfWeek, venueId, ticketingFilter, availableVenues, availableDays, filterBgColor } = this.state;
        const allVenues       = Object.values(availableVenues);
        const hasActiveFilter = fromDate !== "" || toDate !== "" || dayOfWeek !== "" || venueId !== "" || ticketingFilter !== "all";

        const filterBg = filterBgColor || "rgba(0,0,0,0.5)";

        const labelStyle = {
            color:         "rgba(255,255,255,0.7)",
            fontSize:      10,
            fontFamily:    "Raleway",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            display:       "block",
            marginBottom:  4,
        };

        const selectStyle = {
            background:   "#fff",
            border:       "1px solid rgba(255,255,255,0.4)",
            borderRadius: 6,
            color:        "#000",
            padding:      "5px 8px",
            fontSize:     12,
            fontFamily:   "Raleway",
            outline:      "none",
            cursor:       "pointer",
        };

        return (
            <div style={{
                position:            "sticky",
                top:                 0,
                zIndex:              10,
                background:          filterBg,
                backdropFilter:      "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                padding:             "10px 14px 12px",
                display:             "flex",
                flexWrap:            "wrap",
                alignItems:          "flex-end",
                gap:                 12,
                borderBottom:        "1px solid rgba(255,255,255,0.08)",
            }}>
                <div>
                    <span style={labelStyle}>From</span>
                    <input
                        type="date"
                        style={selectStyle}
                        value={fromDate}
                        onChange={e => this.applyFilters({ fromDate: e.target.value })}
                    />
                </div>

                <div>
                    <span style={labelStyle}>To</span>
                    <input
                        type="date"
                        style={selectStyle}
                        value={toDate}
                        onChange={e => this.applyFilters({ toDate: e.target.value })}
                    />
                </div>

                <div>
                    <span style={labelStyle}>Day</span>
                    <select
                        style={selectStyle}
                        value={dayOfWeek}
                        onChange={e => this.applyDayFilter(e.target.value)}
                    >
                        <option value="">All Days</option>
                        {DAY_ORDER.map(d => (
                            <option key={d} value={d}>{d}</option>
                        ))}
                    </select>
                </div>

                {allVenues.length > 0 && (
                    <div>
                        <span style={labelStyle}>Venue</span>
                        <select
                            style={selectStyle}
                            value={venueId}
                            onChange={e => this.applyFilters({ venueId: e.target.value })}
                        >
                            <option value="">All Venues</option>
                            {allVenues.map(v => (
                                <option key={v.id} value={String(v.id)}>{v.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div>
                    <span style={labelStyle}>Events</span>
                    <select
                        style={selectStyle}
                        value={ticketingFilter}
                        onChange={e => this.applyFilters({ ticketingFilter: e.target.value })}
                    >
                        <option value="all">All Events</option>
                        <option value="free">Free</option>
                        <option value="ticketed">Ticketed</option>
                    </select>
                </div>

                {hasActiveFilter && (
                    <button
                        onClick={this.clearFilters}
                        style={{
                            padding:      "5px 12px",
                            borderRadius: 20,
                            border:       "1px solid rgba(255,255,255,0.3)",
                            background:   "transparent",
                            color:        "rgba(255,255,255,0.7)",
                            fontSize:     11,
                            fontFamily:   "Raleway",
                            cursor:       "pointer",
                            whiteSpace:   "nowrap",
                            alignSelf:    "flex-end",
                        }}
                    >
                        ✕ Clear filters
                    </button>
                )}
            </div>
        );
    }

    render() {
        const {
            performData, openDialogDetails, data,
            tableBgColor, tableFtColor, tableWidgetBgColor, enabledTransparent,
            brokenImages, finishLoading, isLoadingMore, hasMore,
        } = this.state;

        const fallbackColor = tableWidgetBgColor !== "transparent" ? tableWidgetBgColor : tableBgColor;

        return (
            <Fragment>
                <div
                    className="gig-table-container-grid"
                    style={{ backgroundColor: enabledTransparent ? "transparent" : tableBgColor, minHeight: 0, marginTop: 0 }}
                >
                    {this.renderFilterBar()}

                    <Grid container justify="flex-start" m={1} spacing={8} style={{ paddingTop: 8 }}>
                        {performData.map((item, index) => (
                            <Grid key={item.eventId || index} item xs={12} md={6} lg={3} className="gridLayout">
                                <div className="gridItem">
                                    <Card className="imageItem" onClick={() => this.handleOpenDetails(item)}>
                                        <CardActionArea>
                                            {brokenImages[item.eventId] ? (
                                                <div className="imageStyle" style={{ backgroundColor: fallbackColor }} />
                                            ) : (
                                                <img
                                                    className="imageStyle"
                                                    src={item.cardImage}
                                                    alt={item.actName}
                                                    onError={() => this.handleImgError(item.eventId)}
                                                />
                                            )}
                                            <div className="topcorner" style={{ backgroundColor: tableWidgetBgColor }}>
                                                <div style={{ fontSize: "19px", fontFamily: "Raleway", fontWeight: "bold", color: tableFtColor }}>
                                                    {`${item.dayShort.toUpperCase()} ${item.date}`}
                                                </div>
                                                <div style={{ fontSize: "15px", fontFamily: "Raleway", fontWeight: "bold", color: tableFtColor }}>
                                                    {item.month}
                                                </div>
                                                <div style={{ fontSize: "11px", fontFamily: "Raleway", fontWeight: "bold", color: tableFtColor }}>
                                                    {item.startTimeString}
                                                </div>
                                            </div>
                                            <div className="bottomText">
                                                <span style={{ fontSize: "20px", fontFamily: "Raleway", fontWeight: "bold", marginBottom: 4, display: "block" }}>
                                                    {item.actName}
                                                </span>
                                                {item.venueName ? (
                                                    <span style={{ fontSize: "13px", fontFamily: "Raleway", color: "rgba(255,255,255,0.8)" }}>
                                                        {item.venueName}
                                                    </span>
                                                ) : null}
                                            </div>
                                            <img className="bottomRightCorner" src={popUpIcon} alt="popup" width={30} height={30} />
                                            <div className="bottomSection" />
                                        </CardActionArea>
                                    </Card>
                                </div>
                            </Grid>
                        ))}

                        {finishLoading && performData.length === 0 && (
                            <Grid item xs={12}>
                                <div style={{ padding: "40px 20px", textAlign: "center", color: tableFtColor, fontFamily: "Raleway" }}>
                                    No upcoming gigs found.
                                </div>
                            </Grid>
                        )}
                    </Grid>

                    <div ref={this.setSentinel} style={{ height: 1 }} />

                    {isLoadingMore && (
                        <div style={{ padding: "24px", textAlign: "center", fontFamily: "Raleway", color: `rgba(${tableFtColor === "#fff" ? "255,255,255" : "0,0,0"},0.6)`, letterSpacing: "0.05em", fontSize: 13 }}>
                            Loading more…
                        </div>
                    )}

                    {finishLoading && !hasMore && performData.length > 0 && (
                        <div style={{ padding: "16px", textAlign: "center", fontFamily: "Raleway", color: `rgba(${tableFtColor === "#fff" ? "255,255,255" : "0,0,0"},0.3)`, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                            All events loaded
                        </div>
                    )}
                </div>

                <div className="embed-footer-grid">
                    <span>
                        <img
                            className="imageFooter"
                            onClick={() => window.open("https://hotgiggity.com", "_system")}
                            alt="hotgiggity logo"
                            src={Logo}
                            height="40px"
                        />
                    </span>
                </div>

                {openDialogDetails && (
                    <EmbedGigGridOrganisationDetails
                        open={openDialogDetails}
                        data={data}
                        tableBgColor={tableBgColor}
                        tableFtColor={tableFtColor}
                        tableWidgetBgColor={tableWidgetBgColor}
                        onClose={this.handleCloseDetails}
                    />
                )}
            </Fragment>
        );
    }
}

export default EmbedGigGridOrganisation;
